import React, { useState, useEffect, useMemo } from 'react';
import { usePermissions } from '../context/PermissionsContext';
import { useAuth } from '../context/AuthContext';
import { reportsAPI } from '../api/client';
import { BarChart3, Inbox, ArrowUpDown, ArrowUp, ArrowDown, TrendingUp, TrendingDown, ChevronLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function Reports() {
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [reportType, setReportType] = useState(location.state?.reportType || 'invoices');
  const [dateRange, setDateRange] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [department, setDepartment] = useState('all');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [sortField, setSortField] = useState('InvoiceDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [yearsBack, setYearsBack] = useState(2);
  const [expandedVendor, setExpandedVendor] = useState(null);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" /> 
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const fmt = (v) => `£${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const pct = (v) => `${(v || 0).toFixed(1)}%`;

  const isYoYReport = reportType === 'yoyBudget' || reportType === 'yoySpending';
  const needsDateRange = !isYoYReport && reportType !== 'aging';

  // Set default date range (current month)
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(lastDay.toISOString().split('T')[0]);
  }, []);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    const now = new Date();
    
    switch(range) {
      case 'week': {
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        setStartDate(weekStart.toISOString().split('T')[0]);
        setEndDate(weekEnd.toISOString().split('T')[0]);
        break;
      }
      case 'month': {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setStartDate(monthStart.toISOString().split('T')[0]);
        setEndDate(monthEnd.toISOString().split('T')[0]);
        break;
      }
      case 'quarter': {
        const quarter = Math.floor(now.getMonth() / 3);
        const quarterStart = new Date(now.getFullYear(), quarter * 3, 1);
        const quarterEnd = new Date(now.getFullYear(), quarter * 3 + 3, 0);
        setStartDate(quarterStart.toISOString().split('T')[0]);
        setEndDate(quarterEnd.toISOString().split('T')[0]);
        break;
      }
      case 'year': {
        setStartDate(new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0]);
        setEndDate(new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0]);
        break;
      }
      case 'lastYear': {
        setStartDate(new Date(now.getFullYear() - 1, 0, 1).toISOString().split('T')[0]);
        setEndDate(new Date(now.getFullYear() - 1, 11, 31).toISOString().split('T')[0]);
        break;
      }
      case 'last6months': {
        const start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(now.toISOString().split('T')[0]);
        break;
      }
      case 'last12months': {
        const start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(now.toISOString().split('T')[0]);
        break;
      }
      default:
        break;
    }
  };

  const generateReport = async () => {
    setLoading(true);
    setReportData(null);
    
    try {
      const params = { startDate, endDate };
      let response;
      
      switch (reportType) {
        case 'invoices':
          if (status !== 'all') params.status = status;
          response = await reportsAPI.getInvoices(params);
          break;
        case 'budgets':
          response = await reportsAPI.getBudgets(params);
          break;
        case 'spending':
          response = await reportsAPI.getSpending(params);
          break;
        case 'vendorAnalysis':
          response = await reportsAPI.getVendorAnalysis(params);
          break;
        case 'glAnalysis':
          response = await reportsAPI.getGLAnalysis(params);
          break;
        case 'yoyBudget':
          response = await reportsAPI.getYoYBudget({ yearsBack });
          break;
        case 'yoySpending':
          response = await reportsAPI.getYoYSpending({ yearsBack });
          break;
        case 'aging':
          response = await reportsAPI.getAging(params);
          break;
        case 'approvalPipeline':
          response = await reportsAPI.getApprovalPipeline(params);
          break;
        default:
          break;
      }
      if (response) setReportData(response.data);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    if (format === 'pdf') return exportPDF();
    try {
      const params = { reportType, startDate, endDate };
      if (status !== 'all') params.status = status;
      if (isYoYReport) params.yearsBack = yearsBack;
      
      const response = await reportsAPI.exportCSV(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}-report-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report. Please try again.');
    }
  };

  const loadLogoAsBase64 = () => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 64;
        canvas.height = img.naturalHeight || 64;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = '/favicon.svg';
    });
  };

  const exportPDF = async () => {
    if (!reportData) return;

    const logoData = await loadLogoAsBase64();
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const companyName = user?.defaultCompany?.companyName || user?.defaultCompany?.CompanyName || '';
    const createdBy = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Unknown';
    const generatedAt = new Date();

    const reportTitles = {
      invoices: 'Invoice Report',
      budgets: 'Budget Report',
      spending: 'Spending Analysis',
      vendorAnalysis: 'Vendor Analysis',
      glAnalysis: 'GL Account Analysis',
      yoyBudget: 'Budget Year-over-Year Comparison',
      yoySpending: 'Spending Year-over-Year Comparison',
      aging: 'Invoice Aging Report',
      approvalPipeline: 'Approval Pipeline Report',
    };

    // ---- Header ----
    let y = 14;

    // Logo (left) + "SpocoDoc" wordmark
    if (logoData) {
      doc.addImage(logoData, 'PNG', 14, y - 3, 10, 10);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(99, 102, 241);
      doc.text('SpocoDoc', 26, y + 3.5);
    }

    // Company name (right-aligned)
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(companyName, pageWidth - 14, y + 2, { align: 'right' });
    y += 10;

    // Thin separator line
    doc.setDrawColor(209, 213, 219);
    doc.setLineWidth(0.5);
    doc.line(14, y, pageWidth - 14, y);
    y += 8;

    // Report title (centered)
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(reportTitles[reportType] || 'Report', pageWidth / 2, y, { align: 'center' });
    y += 7;

    // Date range / period (centered)
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 116, 139);
    if (isYoYReport) {
      doc.text(`Comparing ${yearsBack} years`, pageWidth / 2, y, { align: 'center' });
    } else if (reportType === 'aging') {
      doc.text(`As of ${generatedAt.toLocaleDateString()}`, pageWidth / 2, y, { align: 'center' });
    } else {
      doc.text(`${startDate} to ${endDate}`, pageWidth / 2, y, { align: 'center' });
    }
    y += 6;

    // Created by / date info (centered, smaller)
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(`Created by ${createdBy} on ${generatedAt.toLocaleDateString()} at ${generatedAt.toLocaleTimeString()}`, pageWidth / 2, y, { align: 'center' });
    y += 8;
    doc.setTextColor(0);

    const fmtVal = (v) => `£${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const pctVal = (v) => `${(v || 0).toFixed(1)}%`;

    const addTable = (headers, rows) => {
      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: y,
        styles: { fontSize: 8, cellPadding: 3, lineColor: [229, 231, 235], lineWidth: 0.2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 14, right: 14, bottom: 22 },
        pageBreak: 'auto',
        showHead: 'everyPage',
        didDrawPage: (data) => {
          // On continuation pages, push startY below the repeated header
          if (data.pageNumber > 1) {
            data.settings.startY = 20;
          }
        },
      });
      y = doc.lastAutoTable.finalY + 10;
    };

    const addSectionTitle = (title) => {
      if (y > pageHeight - 35) { doc.addPage(); y = 22; }
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(title, 14, y);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0);
      y += 7;
    };

    // ---- Chart helpers (canvas → PNG → PDF) ----
    const CHART_COLORS = [
      '#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6',
      '#EC4899','#06B6D4','#F97316','#6366F1','#14B8A6',
    ];

    const canvasToImage = (canvas) => canvas.toDataURL('image/png');

    const addChartImage = (canvas, width, height) => {
      if (y + height > pageHeight - 25) { doc.addPage(); y = 22; }
      const imgData = canvasToImage(canvas);
      doc.addImage(imgData, 'PNG', 14, y, width, height);
      y += height + 8;
    };

    // Bar chart: { labels[], datasets: [{ label, data[], color }] }
    const drawBarChart = (labels, datasets, opts = {}) => {
      const dpr = 2;
      const cw = opts.width || 700;
      const ch = opts.height || 300;
      const canvas = document.createElement('canvas');
      canvas.width = cw * dpr; canvas.height = ch * dpr;
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, cw, ch);

      const pad = { top: 30, right: 20, bottom: 50, left: 65 };
      const plotW = cw - pad.left - pad.right;
      const plotH = ch - pad.top - pad.bottom;

      const allVals = datasets.flatMap(d => d.data);
      const maxVal = Math.max(...allVals, 1);

      // Y-axis grid
      ctx.strokeStyle = '#E5E7EB'; ctx.lineWidth = 1;
      ctx.fillStyle = '#6B7280'; ctx.font = '11px sans-serif'; ctx.textAlign = 'right';
      const gridLines = 5;
      for (let i = 0; i <= gridLines; i++) {
        const val = (maxVal / gridLines) * i;
        const yPos = pad.top + plotH - (plotH * (val / maxVal));
        ctx.beginPath(); ctx.moveTo(pad.left, yPos); ctx.lineTo(pad.left + plotW, yPos); ctx.stroke();
        const label = val >= 1000 ? `£${(val/1000).toFixed(0)}k` : val.toFixed(0);
        ctx.fillText(label, pad.left - 6, yPos + 4);
      }

      // Bars
      const nGroups = labels.length;
      const nSets = datasets.length;
      const groupW = plotW / nGroups;
      const barW = Math.min((groupW * 0.7) / nSets, 40);
      const groupOffset = (groupW - barW * nSets) / 2;

      datasets.forEach((ds, si) => {
        ctx.fillStyle = ds.color || CHART_COLORS[si];
        ds.data.forEach((val, i) => {
          const barH = (val / maxVal) * plotH;
          const x = pad.left + i * groupW + groupOffset + si * barW;
          const barY = pad.top + plotH - barH;
          ctx.beginPath();
          const r = Math.min(3, barW / 4);
          ctx.moveTo(x, pad.top + plotH);
          ctx.lineTo(x, barY + r);
          ctx.quadraticCurveTo(x, barY, x + r, barY);
          ctx.lineTo(x + barW - r, barY);
          ctx.quadraticCurveTo(x + barW, barY, x + barW, barY + r);
          ctx.lineTo(x + barW, pad.top + plotH);
          ctx.closePath();
          ctx.fill();
        });
      });

      // X labels
      ctx.fillStyle = '#374151'; ctx.font = '10px sans-serif'; ctx.textAlign = 'center';
      labels.forEach((lbl, i) => {
        const x = pad.left + i * groupW + groupW / 2;
        const maxChars = Math.floor(groupW / 6);
        const display = lbl.length > maxChars ? lbl.slice(0, maxChars - 1) + '…' : lbl;
        ctx.save();
        ctx.translate(x, pad.top + plotH + 10);
        ctx.rotate(-0.4);
        ctx.textAlign = 'right';
        ctx.fillText(display, 0, 0);
        ctx.restore();
      });

      // Legend
      if (nSets > 1) {
        ctx.font = '10px sans-serif'; ctx.textAlign = 'left';
        let lx = pad.left;
        datasets.forEach((ds, si) => {
          ctx.fillStyle = ds.color || CHART_COLORS[si];
          ctx.fillRect(lx, 6, 12, 12);
          ctx.fillStyle = '#374151';
          ctx.fillText(ds.label || '', lx + 16, 16);
          lx += ctx.measureText(ds.label || '').width + 34;
        });
      }

      return canvas;
    };

    // Pie / Donut chart
    const drawPieChart = (labels, values, opts = {}) => {
      const dpr = 2;
      const cw = opts.width || 500;
      const ch = opts.height || 280;
      const canvas = document.createElement('canvas');
      canvas.width = cw * dpr; canvas.height = ch * dpr;
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, cw, ch);

      const total = values.reduce((s, v) => s + v, 0) || 1;
      const cx = ch / 2 + 10;
      const cy = ch / 2;
      const radius = Math.min(cx, cy) - 20;
      const inner = opts.donut ? radius * 0.5 : 0;

      let angle = -Math.PI / 2;
      const slices = [];
      values.forEach((val, i) => {
        const sweep = (val / total) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(cx + inner * Math.cos(angle), cy + inner * Math.sin(angle));
        ctx.arc(cx, cy, radius, angle, angle + sweep);
        ctx.arc(cx, cy, inner, angle + sweep, angle, true);
        ctx.closePath();
        ctx.fillStyle = CHART_COLORS[i % CHART_COLORS.length];
        ctx.fill();
        slices.push({ angle: angle + sweep / 2, color: CHART_COLORS[i % CHART_COLORS.length] });
        angle += sweep;
      });

      // Legend (right side)
      const legendX = ch + 20;
      ctx.font = '11px sans-serif';
      labels.forEach((lbl, i) => {
        const ly = 24 + i * 20;
        ctx.fillStyle = CHART_COLORS[i % CHART_COLORS.length];
        ctx.fillRect(legendX, ly - 10, 12, 12);
        ctx.fillStyle = '#374151'; ctx.textAlign = 'left';
        const pctStr = `${((values[i] / total) * 100).toFixed(1)}%`;
        ctx.fillText(`${lbl} (${pctStr})`, legendX + 18, ly);
      });

      return canvas;
    };

    // Horizontal bar chart
    const drawHorizontalBarChart = (labels, values, opts = {}) => {
      const dpr = 2;
      const barH = 22;
      const gap = 6;
      const pad = { top: 10, right: 80, bottom: 10, left: 140 };
      const cw = opts.width || 700;
      const ch = pad.top + pad.bottom + labels.length * (barH + gap);
      const canvas = document.createElement('canvas');
      canvas.width = cw * dpr; canvas.height = ch * dpr;
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, cw, ch);

      const maxVal = Math.max(...values, 1);
      const plotW = cw - pad.left - pad.right;

      labels.forEach((lbl, i) => {
        const yPos = pad.top + i * (barH + gap);
        const w = (values[i] / maxVal) * plotW;

        // Bar
        ctx.fillStyle = CHART_COLORS[i % CHART_COLORS.length];
        const r = Math.min(4, barH / 3);
        ctx.beginPath();
        ctx.moveTo(pad.left, yPos);
        ctx.lineTo(pad.left + w - r, yPos);
        ctx.quadraticCurveTo(pad.left + w, yPos, pad.left + w, yPos + r);
        ctx.lineTo(pad.left + w, yPos + barH - r);
        ctx.quadraticCurveTo(pad.left + w, yPos + barH, pad.left + w - r, yPos + barH);
        ctx.lineTo(pad.left, yPos + barH);
        ctx.closePath();
        ctx.fill();

        // Label
        ctx.fillStyle = '#374151'; ctx.font = '11px sans-serif'; ctx.textAlign = 'right';
        const maxChars = 22;
        const display = lbl.length > maxChars ? lbl.slice(0, maxChars - 1) + '…' : lbl;
        ctx.fillText(display, pad.left - 6, yPos + barH / 2 + 4);

        // Value
        ctx.fillStyle = '#6B7280'; ctx.textAlign = 'left'; ctx.font = '10px sans-serif';
        const valLabel = values[i] >= 1000 ? `£${(values[i]/1000).toFixed(1)}k` : `£${values[i].toFixed(0)}`;
        ctx.fillText(valLabel, pad.left + w + 6, yPos + barH / 2 + 4);
      });

      return canvas;
    };

    // Helper to add a chart with a given mm width/height
    const addChart = (canvas, mmW, mmH) => {
      if (y + mmH > pageHeight - 25) { doc.addPage(); y = 22; }
      doc.addImage(canvasToImage(canvas), 'PNG', 14, y, mmW || (pageWidth - 28), mmH || 60);
      y += (mmH || 60) + 6;
    };

    switch (reportType) {
      case 'invoices': {
        const s = reportData.summary || {};
        // Status pie chart
        const statusLabels = ['Draft','Pending','Approved','Posted','Rejected'];
        const statusVals = statusLabels.map(st => s[`${st}Count`] || 0);
        if (statusVals.some(v => v > 0)) {
          addSectionTitle('Status Breakdown');
          addChart(drawPieChart(statusLabels, statusVals, { donut: true }), 100, 56);
        }
        addSectionTitle('Summary');
        addTable(['Metric', 'Value'], [
          ['Total Invoices', String(s.TotalInvoices || 0)],
          ['Total Amount', fmtVal(s.TotalAmount)],
          ['Draft', String(s.DraftCount || 0)],
          ['Pending', String(s.PendingCount || 0)],
          ['Approved', String(s.ApprovedCount || 0)],
          ['Posted', String(s.PostedCount || 0)],
          ['Rejected', String(s.RejectedCount || 0)],
          ...(s.PaidCount > 0 || s.UnpaidPostedCount > 0 ? [
            ['— ERP: Paid', `${s.PaidCount || 0} (${fmtVal(s.PaidValue)})`],
            ['— ERP: Partially Paid', String(s.PartiallyPaidCount || 0)],
            ['— ERP: Awaiting Payment', `${s.UnpaidPostedCount || 0} (${fmtVal(s.UnpaidPostedValue)})`],
          ] : []),
        ]);
        if (reportData.trend?.length) {
          addSectionTitle('Monthly Trend');
          addChart(drawBarChart(
            reportData.trend.map(t => t.Month),
            [{ label: 'Amount', data: reportData.trend.map(t => t.Amount || 0) }]
          ), pageWidth - 28, 60);
          addTable(['Month', 'Count', 'Amount'], reportData.trend.map(t => [t.Month, String(t.Count), fmtVal(t.Amount)]));
        }
        break;
      }

      case 'budgets': {
        const s = reportData.summary || {};
        addSectionTitle('Summary');
        addTable(['Metric', 'Value'], [
          ['Total Budget', fmtVal(s.TotalBudget)],
          ['Total Spent', fmtVal(s.TotalSpent)],
          ['Remaining', fmtVal(s.TotalRemaining)],
          ['Utilization Rate', pctVal(s.UtilizationRate)],
        ]);
        if (reportData.departments?.length) {
          addSectionTitle('Department Budget Utilization');
          addChart(drawBarChart(
            reportData.departments.map(d => d.Department),
            [
              { label: 'Budget', data: reportData.departments.map(d => d.Budget || 0), color: '#3B82F6' },
              { label: 'Spent', data: reportData.departments.map(d => d.Spent || 0), color: '#EF4444' },
            ]
          ), pageWidth - 28, 65);
          addTable(['Department', 'Budget', 'Spent', 'Utilization'],
            reportData.departments.map(d => [d.Department, fmtVal(d.Budget), fmtVal(d.Spent), pctVal(d.Utilization)]));
        }
        break;
      }

      case 'spending': {
        if (reportData.topVendors?.length) {
          addSectionTitle('Top Vendors by Spending');
          const topN = reportData.topVendors.slice(0, 10);
          addChart(drawHorizontalBarChart(
            topN.map(v => v.Vendor),
            topN.map(v => v.TotalAmount || 0)
          ), pageWidth - 28, Math.max(30, topN.length * 6 + 6));
          addTable(['Vendor', 'Amount', 'Invoices'],
            reportData.topVendors.map(v => [v.Vendor, fmtVal(v.TotalAmount), String(v.InvoiceCount)]));
        }
        if (reportData.byDepartment?.length) {
          addSectionTitle('Spending by Department');
          addChart(drawPieChart(
            reportData.byDepartment.map(d => d.Department || 'Unassigned'),
            reportData.byDepartment.map(d => d.TotalAmount || 0),
            { donut: true }
          ), 100, 56);
          addTable(['Department', 'Amount', 'Invoices'],
            reportData.byDepartment.map(d => [d.Department, fmtVal(d.TotalAmount), String(d.InvoiceCount)]));
        }
        break;
      }

      case 'vendorAnalysis': {
        if (reportData.vendors?.length) {
          addSectionTitle('Vendor Spending Summary');
          const topVA = reportData.vendors.slice(0, 10);
          addChart(drawHorizontalBarChart(
            topVA.map(v => v.VendorName),
            topVA.map(v => v.TotalSpent || 0)
          ), pageWidth - 28, Math.max(30, topVA.length * 6 + 6));
          addTable(['Vendor', 'Total Spent', 'Invoices', 'Avg Invoice', 'First Invoice', 'Last Invoice'],
            reportData.vendors.map(v => [
              v.VendorName, fmtVal(v.TotalSpent), String(v.InvoiceCount), fmtVal(v.AvgInvoice),
              v.FirstInvoice ? new Date(v.FirstInvoice).toLocaleDateString() : '-',
              v.LastInvoice ? new Date(v.LastInvoice).toLocaleDateString() : '-',
            ]));
        }
        if (reportData.vendorGL?.length) {
          addSectionTitle('Vendor GL Account Breakdown');
          addTable(['Vendor', 'Account Code', 'Account Name', 'Amount', 'Invoices'],
            reportData.vendorGL.map(g => [g.VendorName, g.AccountCode || '-', g.AccountName || 'Unassigned', fmtVal(g.TotalAmount), String(g.InvoiceCount)]));
        }
        if (reportData.vendorDepartments?.length) {
          addSectionTitle('Vendor Department Breakdown');
          addTable(['Vendor', 'Department', 'Amount', 'Invoices'],
            reportData.vendorDepartments.map(d => [d.VendorName, d.Department || 'Unassigned', fmtVal(d.TotalAmount), String(d.InvoiceCount)]));
        }
        if (reportData.vendorTrend?.length) {
          addSectionTitle('Monthly Vendor Spending (Top 5)');
          addTable(['Month', 'Vendor', 'Amount'],
            reportData.vendorTrend.map(t => [t.Month, t.VendorName, fmtVal(t.Amount)]));
        }
        break;
      }

      case 'glAnalysis': {
        if (reportData.glSpending?.length) {
          addSectionTitle('Spending by GL Account');
          const topGL = reportData.glSpending.slice(0, 10);
          addChart(drawHorizontalBarChart(
            topGL.map(gl => `${gl.AccountCode} ${gl.AccountName}`),
            topGL.map(gl => gl.TotalSpent || 0)
          ), pageWidth - 28, Math.max(30, topGL.length * 6 + 6));
          addTable(['Code', 'Account Name', 'Type', 'Total Spent', 'Lines', 'Invoices'],
            reportData.glSpending.map(gl => [gl.AccountCode, gl.AccountName, gl.AccountType, fmtVal(gl.TotalSpent), String(gl.LineCount), String(gl.InvoiceCount)]));
        }
        if (reportData.budgetVsActual?.length) {
          addSectionTitle('Budget vs Actual by GL (Current FY)');
          addChart(drawBarChart(
            reportData.budgetVsActual.map(gl => gl.AccountCode),
            [
              { label: 'Budgeted', data: reportData.budgetVsActual.map(gl => gl.Budgeted || 0), color: '#3B82F6' },
              { label: 'Spent', data: reportData.budgetVsActual.map(gl => gl.Spent || 0), color: '#EF4444' },
            ]
          ), pageWidth - 28, 65);
          addTable(['Code', 'Account', 'Budgeted', 'Spent', 'Remaining', 'Utilization'],
            reportData.budgetVsActual.map(gl => [gl.AccountCode, gl.AccountName, fmtVal(gl.Budgeted), fmtVal(gl.Spent), fmtVal(gl.Remaining), pctVal(gl.Utilization)]));
        }
        if (reportData.glVendors?.length) {
          addSectionTitle('Top Vendors by GL Account');
          addTable(['GL Account', 'Vendor', 'Amount', 'Invoices'],
            reportData.glVendors.map(gv => [`${gv.AccountCode} ${gv.AccountName}`, gv.VendorName, fmtVal(gv.TotalAmount), String(gv.InvoiceCount)]));
        }
        break;
      }

      case 'yoyBudget': {
        if (reportData.budgetByYear?.length) {
          addSectionTitle('Budget Summary by Year');
          addChart(drawBarChart(
            reportData.budgetByYear.map(yr => yr.YearLabel),
            [
              { label: 'Budget', data: reportData.budgetByYear.map(yr => yr.TotalBudget || 0), color: '#3B82F6' },
              { label: 'Spent', data: reportData.budgetByYear.map(yr => yr.TotalSpent || 0), color: '#EF4444' },
            ]
          ), pageWidth - 28, 65);
          addTable(['Year', 'Total Budget', 'Spent', 'Remaining', 'Committed', 'Utilization'],
            reportData.budgetByYear.map(yr => [yr.YearLabel, fmtVal(yr.TotalBudget), fmtVal(yr.TotalSpent), fmtVal(yr.TotalRemaining), fmtVal(yr.TotalCommitted), pctVal(yr.Utilization)]));
        }
        if (reportData.departmentByYear?.length) {
          const years = reportData.budgetByYear?.map(y => y.YearLabel) || [];
          const groups = [...new Set(reportData.departmentByYear.map(d => d.Department))];
          addSectionTitle('Budget by Department');
          const deptHeaders = ['Department'];
          years.forEach(y => { deptHeaders.push(`${y} Budget`); deptHeaders.push(`${y} Spent`); });
          addTable(deptHeaders, groups.map(g => {
            const row = [g];
            years.forEach(y => {
              const cell = reportData.departmentByYear.find(d => d.Department === g && d.YearLabel === y);
              row.push(cell ? fmtVal(cell.Budgeted) : '-');
              row.push(cell ? fmtVal(cell.Spent) : '-');
            });
            return row;
          }));
        }
        if (reportData.glByYear?.length) {
          const years = reportData.budgetByYear?.map(y => y.YearLabel) || [];
          const groups = [...new Set(reportData.glByYear.map(d => d.AccountName))];
          addSectionTitle('Budget by GL Account');
          const glHeaders = ['GL Account'];
          years.forEach(y => { glHeaders.push(`${y} Budget`); glHeaders.push(`${y} Spent`); });
          addTable(glHeaders, groups.map(g => {
            const match = reportData.glByYear.find(d => d.AccountName === g);
            const row = [`${match?.AccountCode || ''} ${g}`];
            years.forEach(y => {
              const cell = reportData.glByYear.find(d => d.AccountName === g && d.YearLabel === y);
              row.push(cell ? fmtVal(cell.Budgeted) : '-');
              row.push(cell ? fmtVal(cell.Spent) : '-');
            });
            return row;
          }));
        }
        break;
      }

      case 'yoySpending': {
        if (reportData.spendingByYear?.length) {
          addSectionTitle('Spending Summary by Year');
          addChart(drawBarChart(
            reportData.spendingByYear.map(yr => yr.YearLabel),
            [{ label: 'Total Spent', data: reportData.spendingByYear.map(yr => yr.TotalSpent || 0) }]
          ), pageWidth - 28, 65);
          addTable(['Year', 'Total Spent', 'Invoices', 'Avg Invoice', 'Vendors'],
            reportData.spendingByYear.map(yr => [yr.YearLabel, fmtVal(yr.TotalSpent), String(yr.InvoiceCount), fmtVal(yr.AvgInvoice), String(yr.UniqueVendors)]));
        }
        if (reportData.monthlyByYear?.length) {
          const years = [...new Set(reportData.monthlyByYear.map(m => m.YearLabel))];
          const months = [...new Set(reportData.monthlyByYear.map(m => m.MonthNum))].sort((a, b) => a - b);
          addSectionTitle('Monthly Spending Comparison');
          addTable(['Month', ...years], months.map(mn => {
            const row = reportData.monthlyByYear.find(m => m.MonthNum === mn);
            const cells = [row?.MonthName || `Month ${mn}`];
            years.forEach(y => {
              const cell = reportData.monthlyByYear.find(m => m.YearLabel === y && m.MonthNum === mn);
              cells.push(cell ? fmtVal(cell.TotalSpent) : '-');
            });
            return cells;
          }));
        }
        if (reportData.vendorsByYear?.length) {
          addSectionTitle('Top 5 Vendors by Year');
          addTable(['Year', 'Vendor', 'Total Spent'],
            reportData.vendorsByYear.map(v => [v.YearLabel, v.VendorName, fmtVal(v.TotalSpent)]));
        }
        break;
      }

      case 'aging': {
        const s = reportData.summary || {};
        addSectionTitle('Summary');
        addTable(['Metric', 'Value'], [
          ['Total Unposted Invoices', String(s.TotalUnpaid || 0)],
          ['Outstanding Amount', fmtVal(s.TotalOutstanding)],
          ['Average Age', `${s.AvgAgeDays || 0} days`],
          ['Oldest Invoice', `${s.OldestDays || 0} days`],
        ]);
        if (reportData.agingBuckets?.length) {
          addSectionTitle('Aging Buckets');
          addChart(drawBarChart(
            reportData.agingBuckets.map(b => b.AgeBucket),
            [{ label: 'Outstanding', data: reportData.agingBuckets.map(b => b.TotalAmount || 0) }],
            { height: 250 }
          ), pageWidth - 28, 55);
          addTable(['Age Bucket', 'Amount', 'Invoices'],
            reportData.agingBuckets.map(b => [b.AgeBucket, fmtVal(b.TotalAmount), String(b.InvoiceCount)]));
        }
        if (reportData.agingByVendor?.length) {
          addSectionTitle('Aging by Vendor');
          addTable(['Vendor', 'Current', '31-60 Days', '61-90 Days', '90+ Days', 'Total'],
            reportData.agingByVendor.map(v => [v.VendorName, fmtVal(v.Current), fmtVal(v.Days31to60), fmtVal(v.Days61to90), fmtVal(v.Over90), fmtVal(v.Total)]));
        }
        break;
      }

      case 'approvalPipeline': {
        const p = reportData.processing || {};
        addSectionTitle('Processing Statistics');
        addTable(['Metric', 'Value'], [
          ['Avg Processing Time', `${p.AvgProcessingDays || 0} days`],
          ['Min Processing Time', `${p.MinProcessingDays || 0} days`],
          ['Max Processing Time', `${p.MaxProcessingDays || 0} days`],
          ['Stale Invoices (>7 days)', String(p.StaleCount || 0)],
        ]);
        if (reportData.statusDistribution?.length) {
          addSectionTitle('Status Distribution');
          addChart(drawPieChart(
            reportData.statusDistribution.map(s => s.Status),
            reportData.statusDistribution.map(s => s.Count || 0),
            { donut: true }
          ), 100, 56);
          addTable(['Status', 'Count', 'Total Amount'],
            reportData.statusDistribution.map(s => [s.Status, String(s.Count), fmtVal(s.TotalAmount)]));
        }
        if (reportData.departmentApproval?.length) {
          addSectionTitle('Approval Rate by Department');
          addTable(['Department', 'Total', 'Approved', 'Rejected', 'Pending', 'Approval Rate'],
            reportData.departmentApproval.map(d => [d.Department || 'Unassigned', String(d.Total), String(d.Approved), String(d.Rejected), String(d.Pending), pctVal(d.ApprovalRate)]));
        }
        if (reportData.volumeTrend?.length) {
          addSectionTitle('Monthly Volume Trend');
          addTable(['Month', 'Submitted', 'Approved', 'Rejected'],
            reportData.volumeTrend.map(v => [v.Month, String(v.Submitted), String(v.Processed), String(v.Rejected)]));
        }
        break;
      }

      default:
        break;
    }

    // Add header, footer, and page numbers to every page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      // Repeat header on continuation pages
      if (i > 1) {
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        if (logoData) {
          doc.addImage(logoData, 'PNG', 14, 6, 22, 8);
        }
        doc.text(companyName, pageWidth - 14, 12, { align: 'right' });
        doc.setDrawColor(230, 232, 236);
        doc.setLineWidth(0.3);
        doc.line(14, 17, pageWidth - 14, 17);
      }

      // Footer separator
      doc.setDrawColor(230, 232, 236);
      doc.setLineWidth(0.3);
      doc.line(14, pageHeight - 16, pageWidth - 14, pageHeight - 16);

      // Footer text
      doc.setFontSize(7);
      doc.setTextColor(160);
      doc.text(`${companyName} — Confidential`, 14, pageHeight - 11);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 11, { align: 'center' });
      doc.text(`${generatedAt.toLocaleDateString()} ${generatedAt.toLocaleTimeString()}`, pageWidth - 14, pageHeight - 11, { align: 'right' });
    }

    doc.save(`${reportType}-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Generic sort helper
  const sortArray = (arr, field, dir) => {
    if (!arr) return [];
    return [...arr].sort((a, b) => {
      let aVal = a[field], bVal = b[field];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return dir === 'asc' ? -1 : 1;
      if (aVal > bVal) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedTrendData = useMemo(() => sortArray(reportData?.trend, sortField, sortDirection), [reportData?.trend, sortField, sortDirection]);
  const sortedDepartments = useMemo(() => sortArray(reportData?.departments, sortField, sortDirection), [reportData?.departments, sortField, sortDirection]);
  const sortedTopVendors = useMemo(() => sortArray(reportData?.topVendors, sortField, sortDirection), [reportData?.topVendors, sortField, sortDirection]);

  return (
    <div className="space-y-6">
      {/* Back navigation */}
      {location.state?.from && (
        <button
          onClick={() => navigate(location.state.from)}
          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium group"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to {location.state.fromLabel || 'Previous Page'}
        </button>
      )}
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 className="w-7 h-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-500">Generate and export financial reports</p>
        </div>
      </div>

      {/* Report Configuration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Report Configuration</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Report Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => { setReportType(e.target.value); setReportData(null); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <optgroup label="Core Reports">
                <option value="invoices">Invoice Report</option>
                {hasPermission('view_budgets') && <option value="budgets">Budget Report</option>}
                <option value="spending">Spending Analysis</option>
              </optgroup>
              <optgroup label="Detailed Analysis">
                <option value="vendorAnalysis">Vendor Analysis</option>
                <option value="glAnalysis">GL Account Analysis</option>
                <option value="aging">Invoice Aging</option>
                <option value="approvalPipeline">Approval Pipeline</option>
              </optgroup>
              <optgroup label="Year-over-Year">
                {hasPermission('view_budgets') && <option value="yoyBudget">Budget YoY Comparison</option>}
                <option value="yoySpending">Spending YoY Comparison</option>
              </optgroup>
            </select>
          </div>

          {/* YoY: Years Back selector */}
          {isYoYReport && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Years to Compare</label>
              <select
                value={yearsBack}
                onChange={(e) => setYearsBack(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {[2,3,4,5].map(n => <option key={n} value={n}>{n} Years</option>)}
              </select>
            </div>
          )}

          {/* Date Range Preset — hide for YoY and Aging */}
          {needsDateRange && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Period</label>
                <select
                  value={dateRange}
                  onChange={(e) => handleDateRangeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="quarter">This Quarter</option>
                  <option value="year">This Year</option>
                  <option value="lastYear">Last Year</option>
                  <option value="last6months">Last 6 Months</option>
                  <option value="last12months">Last 12 Months</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </>
          )}
        </div>

        {/* Invoice-specific filters */}
        {reportType === 'invoices' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status Filter</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="posted">Posted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <button onClick={generateReport} disabled={loading} className="btn btn-primary">
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
          {reportData && (
            <>
              <button onClick={() => exportReport('csv')} className="btn btn-success">Export CSV</button>
              <button onClick={() => exportReport('pdf')} className="btn btn-danger">Export PDF</button>
            </>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Generating report...</p>
        </div>
      )}

      {/* ========== INVOICE REPORT ========== */}
      {!loading && reportData && reportType === 'invoices' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <SummaryCard label="Total Invoices" value={reportData.summary?.TotalInvoices || 0} />
            <SummaryCard label="Total Amount" value={fmt(reportData.summary?.TotalAmount)} color="green" />
            <SummaryCard label="Pending" value={reportData.summary?.PendingCount || 0} color="yellow" />
            <SummaryCard label="Posted" value={reportData.summary?.PostedCount || 0} color="blue" />
          </div>

          {/* Payment status summary — only show if ERP data exists */}
          {(reportData.summary?.PaidCount > 0 || reportData.summary?.UnpaidPostedCount > 0 || reportData.summary?.PartiallyPaidCount > 0) && (
            <div className="bg-white rounded-lg shadow-sm border border-green-100 p-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">ERP Payment Status (Posted Invoices)</h3>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-emerald-50 rounded-lg px-4 py-2.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <div>
                    <p className="text-lg font-bold text-emerald-700">{reportData.summary?.PaidCount || 0}</p>
                    <p className="text-xs text-emerald-600">Paid — {fmt(reportData.summary?.PaidValue)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-amber-50 rounded-lg px-4 py-2.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <div>
                    <p className="text-lg font-bold text-amber-700">{reportData.summary?.PartiallyPaidCount || 0}</p>
                    <p className="text-xs text-amber-600">Partially Paid</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-red-50 rounded-lg px-4 py-2.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div>
                    <p className="text-lg font-bold text-red-700">{reportData.summary?.UnpaidPostedCount || 0}</p>
                    <p className="text-xs text-red-600">Awaiting Payment — {fmt(reportData.summary?.UnpaidPostedValue)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Invoice Status Breakdown</h3>
            <div className="space-y-3">
              {reportData.summary && ['Draft','Pending','Approved','Posted','Rejected'].map(s => {
                const cnt = reportData.summary[`${s}Count`] || 0;
                const total = reportData.summary.TotalInvoices || 1;
                return (
                  <div key={s} className="flex items-center justify-between">
                    <span className="text-gray-700">{s}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-48 bg-gray-200 rounded-full h-2">
                        <div className="bg-primary-600 h-2 rounded-full" style={{ width: `${(cnt/total)*100}%` }} />
                      </div>
                      <span className="font-semibold w-12 text-right">{cnt}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <DataTable title="Monthly Trend" data={sortedTrendData} columns={[
            { key: 'Month', label: 'Month' },
            { key: 'Count', label: 'Count' },
            { key: 'Amount', label: 'Amount', render: v => fmt(v), align: 'right' },
          ]} handleSort={handleSort} SortIcon={SortIcon} />
        </div>
      )}

      {/* ========== BUDGET REPORT ========== */}
      {!loading && reportData && reportType === 'budgets' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <SummaryCard label="Total Budget" value={fmt(reportData.summary?.TotalBudget)} />
            <SummaryCard label="Total Spent" value={fmt(reportData.summary?.TotalSpent)} color="red" />
            <SummaryCard label="Remaining" value={fmt(reportData.summary?.TotalRemaining)} color="green" />
            <SummaryCard label="Utilization Rate" value={pct(reportData.summary?.UtilizationRate)} color="blue" />
          </div>

          <DataTable title="Department Budget Utilization" data={sortedDepartments} columns={[
            { key: 'Department', label: 'Department' },
            { key: 'Budget', label: 'Budget', render: v => fmt(v), align: 'right' },
            { key: 'Spent', label: 'Spent', render: v => fmt(v), align: 'right' },
            { key: 'Utilization', label: 'Utilization', render: v => <span className={`font-semibold ${v > 90 ? 'text-red-600' : v > 70 ? 'text-yellow-600' : 'text-green-600'}`}>{pct(v)}</span>, align: 'right' },
          ]} handleSort={handleSort} SortIcon={SortIcon} />
        </div>
      )}

      {/* ========== SPENDING ANALYSIS ========== */}
      {!loading && reportData && reportType === 'spending' && (
        <div className="space-y-6">
          <DataTable title="Top Vendors by Spending" data={sortedTopVendors} columns={[
            { key: 'Vendor', label: 'Vendor' },
            { key: 'TotalAmount', label: 'Amount', render: v => fmt(v), align: 'right' },
            { key: 'InvoiceCount', label: 'Invoices', align: 'right' },
          ]} handleSort={handleSort} SortIcon={SortIcon} />

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Spending by Department</h3>
            <div className="space-y-3">
              {reportData.byDepartment?.map((dept, i) => {
                const total = reportData.byDepartment.reduce((s, d) => s + (d.TotalAmount || 0), 0);
                const pctVal = total > 0 ? (dept.TotalAmount / total) * 100 : 0;
                return (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-gray-700">{dept.Department}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-48 bg-gray-200 rounded-full h-2">
                        <div className="bg-primary-600 h-2 rounded-full" style={{ width: `${pctVal}%` }} />
                      </div>
                      <span className="font-semibold w-24 text-right">{fmt(dept.TotalAmount)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ========== VENDOR ANALYSIS ========== */}
      {!loading && reportData && reportType === 'vendorAnalysis' && (
        <div className="space-y-6">
          {/* Vendor Summary Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Vendor Spending Summary</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vendor</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Spent</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoices</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg Invoice</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">First Invoice</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Invoice</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reportData.vendors?.map((v, i) => (
                    <React.Fragment key={i}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{v.VendorName}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{fmt(v.TotalSpent)}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{v.InvoiceCount}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{fmt(v.AvgInvoice)}</td>
                        <td className="px-4 py-3 text-center text-gray-500 text-xs">{v.FirstInvoice ? new Date(v.FirstInvoice).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3 text-center text-gray-500 text-xs">{v.LastInvoice ? new Date(v.LastInvoice).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => setExpandedVendor(expandedVendor === v.VendorName ? null : v.VendorName)}
                            className="text-xs text-blue-600 hover:underline">
                            {expandedVendor === v.VendorName ? 'Hide' : 'Expand'}
                          </button>
                        </td>
                      </tr>
                      {expandedVendor === v.VendorName && (
                        <tr>
                          <td colSpan={7} className="px-4 py-3 bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* GL Accounts for this vendor */}
                              <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-2">GL Accounts / Services</h4>
                                <table className="w-full text-xs">
                                  <thead><tr className="border-b"><th className="text-left py-1">Code</th><th className="text-left py-1">Account</th><th className="text-right py-1">Amount</th><th className="text-right py-1">Invoices</th></tr></thead>
                                  <tbody>
                                    {reportData.vendorGL?.filter(g => g.VendorName === v.VendorName).map((g, j) => (
                                      <tr key={j} className="border-b border-gray-100">
                                        <td className="py-1 text-gray-600">{g.AccountCode || '-'}</td>
                                        <td className="py-1">{g.AccountName || 'Unassigned'}</td>
                                        <td className="py-1 text-right">{fmt(g.TotalAmount)}</td>
                                        <td className="py-1 text-right">{g.InvoiceCount}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {/* Departments for this vendor */}
                              <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-2">Departments</h4>
                                <table className="w-full text-xs">
                                  <thead><tr className="border-b"><th className="text-left py-1">Department</th><th className="text-right py-1">Amount</th><th className="text-right py-1">Invoices</th></tr></thead>
                                  <tbody>
                                    {reportData.vendorDepartments?.filter(d => d.VendorName === v.VendorName).map((d, j) => (
                                      <tr key={j} className="border-b border-gray-100">
                                        <td className="py-1">{d.Department || 'Unassigned'}</td>
                                        <td className="py-1 text-right">{fmt(d.TotalAmount)}</td>
                                        <td className="py-1 text-right">{d.InvoiceCount}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Vendor Spending Trend (Top 5) */}
          {reportData.vendorTrend?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">Monthly Vendor Spending (Top 5)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Month</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Vendor</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reportData.vendorTrend.map((t, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-900">{t.Month}</td>
                        <td className="px-4 py-2 text-gray-900">{t.VendorName}</td>
                        <td className="px-4 py-2 text-right text-gray-900">{fmt(t.Amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== GL ACCOUNT ANALYSIS ========== */}
      {!loading && reportData && reportType === 'glAnalysis' && (
        <div className="space-y-6">
          {/* GL Spending */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Spending by GL Account</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Account Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Total Spent</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Lines</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Invoices</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reportData.glSpending?.map((gl, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-gray-600">{gl.AccountCode}</td>
                      <td className="px-4 py-3 text-gray-900">{gl.AccountName}</td>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{gl.AccountType}</span></td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmt(gl.TotalSpent)}</td>
                      <td className="px-4 py-3 text-right text-gray-900">{gl.LineCount}</td>
                      <td className="px-4 py-3 text-right text-gray-900">{gl.InvoiceCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Budget vs Actual */}
          {reportData.budgetVsActual?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">Budget vs Actual by GL (Current FY)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Code</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Account</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Budgeted</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Spent</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Remaining</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Utilization</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reportData.budgetVsActual.map((gl, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-gray-600">{gl.AccountCode}</td>
                        <td className="px-4 py-3 text-gray-900">{gl.AccountName}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{fmt(gl.Budgeted)}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{fmt(gl.Spent)}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{fmt(gl.Remaining)}</td>
                        <td className="px-4 py-3 text-right">
                          <UtilBadge value={gl.Utilization} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top Vendors per GL */}
          {reportData.glVendors?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">Top Vendors by GL Account</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">GL Account</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Vendor</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Invoices</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reportData.glVendors.map((gv, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-600"><span className="font-mono">{gv.AccountCode}</span> {gv.AccountName}</td>
                        <td className="px-4 py-3 text-gray-900">{gv.VendorName}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{fmt(gv.TotalAmount)}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{gv.InvoiceCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== YoY BUDGET ========== */}
      {!loading && reportData && reportType === 'yoyBudget' && (
        <div className="space-y-6">
          {/* Year comparison cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reportData.budgetByYear?.map((yr, i) => (
              <div key={i} className={`bg-white rounded-lg shadow-sm border p-5 ${yr.IsCurrent ? 'border-blue-300 ring-1 ring-blue-200' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700">{yr.YearLabel}</span>
                  {yr.IsCurrent && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Current</span>}
                </div>
                <p className="text-xl font-bold text-gray-900">{fmt(yr.TotalBudget)}</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-gray-500">Spent:</span> <span className="font-semibold text-red-600">{fmt(yr.TotalSpent)}</span></div>
                  <div><span className="text-gray-500">Remaining:</span> <span className="font-semibold text-green-600">{fmt(yr.TotalRemaining)}</span></div>
                  <div><span className="text-gray-500">Committed:</span> <span className="font-semibold">{fmt(yr.TotalCommitted)}</span></div>
                  <div><span className="text-gray-500">Utilization:</span> <UtilBadge value={yr.Utilization} /></div>
                </div>
              </div>
            ))}
          </div>

          {/* Department by Year */}
          {reportData.departmentByYear?.length > 0 && (
            <YoYTable
              title="Budget by Department"
              data={reportData.departmentByYear}
              years={reportData.budgetByYear?.map(y => y.YearLabel) || []}
              groupKey="Department"
              valueKey="Budgeted"
              secondaryKey="Spent"
              secondaryLabel="Spent"
              fmt={fmt}
            />
          )}

          {/* GL by Year */}
          {reportData.glByYear?.length > 0 && (
            <YoYTable
              title="Budget by GL Account"
              data={reportData.glByYear}
              years={reportData.budgetByYear?.map(y => y.YearLabel) || []}
              groupKey="AccountName"
              groupCode="AccountCode"
              valueKey="Budgeted"
              secondaryKey="Spent"
              secondaryLabel="Spent"
              fmt={fmt}
            />
          )}
        </div>
      )}

      {/* ========== YoY SPENDING ========== */}
      {!loading && reportData && reportType === 'yoySpending' && (
        <div className="space-y-6">
          {/* Year comparison cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reportData.spendingByYear?.map((yr, i) => {
              const prev = reportData.spendingByYear[i + 1];
              const change = prev?.TotalSpent ? ((yr.TotalSpent - prev.TotalSpent) / prev.TotalSpent * 100) : null;
              return (
                <div key={i} className={`bg-white rounded-lg shadow-sm border p-5 ${yr.IsCurrent ? 'border-blue-300 ring-1 ring-blue-200' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-700">{yr.YearLabel}</span>
                    {yr.IsCurrent && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Current</span>}
                  </div>
                  <p className="text-xl font-bold text-gray-900">{fmt(yr.TotalSpent)}</p>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-gray-500">Invoices:</span> <span className="font-semibold">{yr.InvoiceCount}</span></div>
                    <div><span className="text-gray-500">Avg Invoice:</span> <span className="font-semibold">{fmt(yr.AvgInvoice)}</span></div>
                    <div><span className="text-gray-500">Vendors:</span> <span className="font-semibold">{yr.UniqueVendors}</span></div>
                    {change !== null && (
                      <div className="flex items-center gap-1">
                        {change >= 0 ? <TrendingUp className="w-3 h-3 text-red-500" /> : <TrendingDown className="w-3 h-3 text-green-500" />}
                        <span className={`font-semibold ${change >= 0 ? 'text-red-600' : 'text-green-600'}`}>{change >= 0 ? '+' : ''}{change.toFixed(1)}%</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Monthly overlay */}
          {reportData.monthlyByYear?.length > 0 && (() => {
            const years = [...new Set(reportData.monthlyByYear.map(m => m.YearLabel))];
            const months = [...new Set(reportData.monthlyByYear.map(m => m.MonthNum))].sort((a,b) => a - b);
            return (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4">Monthly Spending Comparison</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Month</th>
                        {years.map(y => <th key={y} className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">{y}</th>)}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {months.map(mn => {
                        const row = reportData.monthlyByYear.find(m => m.MonthNum === mn);
                        return (
                          <tr key={mn} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-900">{row?.MonthName || `Month ${mn}`}</td>
                            {years.map(y => {
                              const cell = reportData.monthlyByYear.find(m => m.YearLabel === y && m.MonthNum === mn);
                              return <td key={y} className="px-4 py-2 text-right text-gray-900">{cell ? fmt(cell.TotalSpent) : '-'}</td>;
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Top vendors per year */}
          {reportData.vendorsByYear?.length > 0 && (() => {
            const years = [...new Set(reportData.vendorsByYear.map(v => v.YearLabel))];
            return (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4">Top 5 Vendors by Year</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {years.map(y => (
                    <div key={y}>
                      <h4 className="text-sm font-semibold text-gray-600 mb-2">{y}</h4>
                      <div className="space-y-1">
                        {reportData.vendorsByYear.filter(v => v.YearLabel === y).map((v, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-gray-700 truncate mr-2">{v.VendorName}</span>
                            <span className="font-semibold text-gray-900 whitespace-nowrap">{fmt(v.TotalSpent)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ========== AGING REPORT ========== */}
      {!loading && reportData && reportType === 'aging' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <SummaryCard label="Total Unposted" value={reportData.summary?.TotalUnpaid || 0} />
            <SummaryCard label="Outstanding" value={fmt(reportData.summary?.TotalOutstanding)} color="red" />
            <SummaryCard label="Avg Age" value={`${reportData.summary?.AvgAgeDays || 0} days`} color="yellow" />
            <SummaryCard label="Oldest" value={`${reportData.summary?.OldestDays || 0} days`} color="red" />
          </div>

          {/* Aging Buckets */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Aging Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {reportData.agingBuckets?.map((bucket, i) => {
                const colors = ['bg-green-50 border-green-200 text-green-800', 'bg-yellow-50 border-yellow-200 text-yellow-800', 'bg-orange-50 border-orange-200 text-orange-800', 'bg-red-50 border-red-200 text-red-800'];
                return (
                  <div key={i} className={`rounded-lg border p-4 ${colors[i] || colors[3]}`}>
                    <p className="text-xs font-medium uppercase">{bucket.AgeBucket}</p>
                    <p className="text-xl font-bold mt-1">{fmt(bucket.TotalAmount)}</p>
                    <p className="text-xs mt-1">{bucket.InvoiceCount} invoice{bucket.InvoiceCount !== 1 ? 's' : ''}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Aging by Vendor */}
          {reportData.agingByVendor?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">Aging by Vendor</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Vendor</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Current</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">31-60 Days</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">61-90 Days</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">90+ Days</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reportData.agingByVendor.map((v, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{v.VendorName}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{fmt(v.Current)}</td>
                        <td className="px-4 py-3 text-right text-yellow-700">{fmt(v.Days31to60)}</td>
                        <td className="px-4 py-3 text-right text-orange-700">{fmt(v.Days61to90)}</td>
                        <td className="px-4 py-3 text-right text-red-700 font-semibold">{fmt(v.Over90)}</td>
                        <td className="px-4 py-3 text-right font-bold text-gray-900">{fmt(v.Total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== APPROVAL PIPELINE ========== */}
      {!loading && reportData && reportType === 'approvalPipeline' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <SummaryCard label="Avg Processing" value={`${reportData.processing?.AvgProcessingDays || 0} days`} />
            <SummaryCard label="Min Processing" value={`${reportData.processing?.MinProcessingDays || 0} days`} color="green" />
            <SummaryCard label="Max Processing" value={`${reportData.processing?.MaxProcessingDays || 0} days`} color="red" />
            <SummaryCard label="Stale (>7d)" value={reportData.processing?.StaleCount || 0} color="yellow" />
          </div>

          {/* Status Distribution */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Status Distribution</h3>
            <div className="space-y-3">
              {reportData.statusDistribution?.map((s, i) => {
                const total = reportData.statusDistribution.reduce((sum, x) => sum + x.Count, 0);
                const pctVal = total > 0 ? (s.Count / total * 100) : 0;
                const statusColors = { Draft: 'bg-gray-400', Pending: 'bg-yellow-500', Approved: 'bg-green-500', Posted: 'bg-blue-500', Rejected: 'bg-red-500' };
                return (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-gray-700 w-24">{s.Status}</span>
                    <div className="flex items-center gap-3 flex-1">
                      <div className="flex-1 bg-gray-200 rounded-full h-3 mx-4">
                        <div className={`h-3 rounded-full ${statusColors[s.Status] || 'bg-gray-400'}`} style={{ width: `${pctVal}%` }} />
                      </div>
                      <span className="font-semibold w-12 text-right">{s.Count}</span>
                      <span className="text-gray-500 text-xs w-20 text-right">{fmt(s.TotalAmount)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Approval Rate by Department */}
          {reportData.departmentApproval?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">Approval Rate by Department</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Department</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Approved</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Rejected</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Pending</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Approval Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reportData.departmentApproval.map((d, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-900">{d.Department || 'Unassigned'}</td>
                        <td className="px-4 py-3 text-right text-gray-900">{d.Total}</td>
                        <td className="px-4 py-3 text-right text-green-700">{d.Approved}</td>
                        <td className="px-4 py-3 text-right text-red-700">{d.Rejected}</td>
                        <td className="px-4 py-3 text-right text-yellow-700">{d.Pending}</td>
                        <td className="px-4 py-3 text-right"><UtilBadge value={d.ApprovalRate} greenThreshold={80} yellowThreshold={60} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Volume Trend */}
          {reportData.volumeTrend?.length > 0 && (
            <DataTable title="Monthly Volume" data={reportData.volumeTrend} columns={[
              { key: 'Month', label: 'Month' },
              { key: 'Submitted', label: 'Submitted', align: 'right' },
              { key: 'Processed', label: 'Approved', align: 'right' },
              { key: 'Rejected', label: 'Rejected', align: 'right' },
            ]} handleSort={handleSort} SortIcon={SortIcon} />
          )}
        </div>
      )}

      {/* No report generated yet */}
      {!loading && !reportData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="font-medium text-gray-900">No report generated</p>
          <p className="text-sm text-gray-500 mt-1">Configure your report parameters above and click "Generate Report"</p>
        </div>
      )}
    </div>
  );
}

// ---- Reusable sub-components ----

function SummaryCard({ label, value, color }) {
  const colors = { green: 'text-green-600', red: 'text-red-600', blue: 'text-blue-600', yellow: 'text-yellow-600' };
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${colors[color] || 'text-gray-900'}`}>{value}</p>
    </div>
  );
}

function UtilBadge({ value, greenThreshold = 70, yellowThreshold = 90 }) {
  const v = value || 0;
  const cls = v > yellowThreshold ? 'text-red-600' : v > greenThreshold ? 'text-yellow-600' : 'text-green-600';
  return <span className={`font-semibold ${cls}`}>{v.toFixed(1)}%</span>;
}

function DataTable({ title, data, columns, handleSort, SortIcon }) {
  if (!data?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              {columns.map(col => (
                <th key={col.key} className={`px-4 py-3 ${col.align === 'right' ? 'text-right' : 'text-left'} text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none`}
                  onClick={() => handleSort(col.key)}>
                  <span className="inline-flex items-center">{col.label} <SortIcon field={col.key} /></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50">
                {columns.map(col => (
                  <td key={col.key} className={`px-4 py-3 whitespace-nowrap text-gray-900 ${col.align === 'right' ? 'text-right' : ''}`}>
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function YoYTable({ title, data, years, groupKey, groupCode, valueKey, secondaryKey, secondaryLabel, fmt }) {
  const groups = [...new Set(data.map(d => d[groupKey]))];
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{groupKey === 'AccountName' ? 'GL Account' : groupKey}</th>
              {years.map(y => (
                <React.Fragment key={y}>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">{y} Budget</th>
                  {secondaryKey && <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">{y} {secondaryLabel}</th>}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {groups.map((g, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-900">
                  {groupCode && <span className="font-mono text-gray-500 mr-2">{data.find(d => d[groupKey] === g)?.[groupCode]}</span>}
                  {g}
                </td>
                {years.map(y => {
                  const cell = data.find(d => d[groupKey] === g && d.YearLabel === y);
                  return (
                    <React.Fragment key={y}>
                      <td className="px-4 py-3 text-right text-gray-900">{cell ? fmt(cell[valueKey]) : '-'}</td>
                      {secondaryKey && <td className="px-4 py-3 text-right text-gray-600">{cell ? fmt(cell[secondaryKey]) : '-'}</td>}
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
